import { useState } from 'react'
import type { JSX } from 'react'
import { api, call } from '@/api/client'
import { useMutation, useQuery } from '@/api/query'
import { may, useTenants } from '@/tenant/context'
import { Button } from '@/ui/Button'
import { Field } from '@/ui/Field'
import { ErrorState, InlineError, Loading } from '@/ui/states'
import { useToast } from '@/ui/toast-context'

/**
 * Send window — C1, the card `design/screens/outreach.html#health` draws beside
 * account health, and the third ban-risk control in the product.
 *
 * The mockup states the reason in the field's own help text and this panel keeps
 * it verbatim: *"IST. Activity outside business hours is a strong ban signal."*
 * That is not decoration. `app/linkedin/limits.py` refuses every search and every
 * post outside these hours, so what this card shows is a rule the server actually
 * enforces rather than a preference it records.
 *
 * **It lives on the outreach dashboard and writes to `/api/v1/automation`**, which
 * looks like a mismatch and is not. The hours are three columns on the
 * `automation` singleton — the same row behind Settings → Danger zone's *Pause all
 * automation* — because they answer the same question with the clock added.
 * `app/v1/automation.py` argues that; the short form is that the screen a control
 * appears on is a fact about the UI and the resource it belongs to is a fact about
 * the model.
 *
 * **Selects, not a time input.** The mockup draws two `<select>`s and this follows
 * it, for a reason beyond fidelity: `<input type="time">` renders as a
 * browser-chrome spinner that differs on every platform, accepts 03:07, and would
 * invite a tenant to set a window to the minute when the thing being configured is
 * *roughly which part of the day*. Half-hour steps make the set of valid answers
 * visible and make the common ones one click away.
 *
 * **Everything is editable by `budget:manage` and visible to everyone**, which is
 * the line the server draws and the same one the Danger zone's switch uses. A
 * member who cannot widen the window still needs to know why discovery stopped at
 * half past six.
 *
 * **Save is explicit, and the button stays disabled until something changes.**
 * The alternative — saving on every `onChange` — would write three §8 audit
 * entries while somebody scrolled a dropdown, and this is an audited control
 * precisely because *who widened this window* is the first question asked after an
 * account is interrupted.
 */
export function SendWindowPanel(): JSX.Element {
  // **`useTenants` rather than `useActiveTenant`, and the difference matters
  // here.** `useActiveTenant` throws when no organisation is resolved yet, which
  // is correct for a screen the tenant guard has vetted — but this panel is one
  // of five on a dashboard that renders while its own queries are still in
  // flight, and a panel that throws during that frame takes the other four down
  // with it. No active tenant means no role, which means not editable, which is
  // exactly what should be rendered in a frame where nothing is known yet.
  const { active } = useTenants()
  const toast = useToast()
  const editable = active !== null && may(active.membership.role, 'budget:manage')

  const automation = useQuery((signal) => call(() => api.GET('/api/v1/automation', { signal })), [])
  const save = useMutation((body: { window_start: string; window_end: string; skip: boolean }) =>
    call(() =>
      api.PUT('/api/v1/automation/send-window', {
        body: {
          window_start: body.window_start,
          window_end: body.window_end,
          skip_weekends: body.skip,
        },
      }),
    ),
  )

  const saved = automation.state.data ?? null
  const savedStart = saved === null ? null : trimSeconds(saved.window_start)
  const savedEnd = saved === null ? null : trimSeconds(saved.window_end)
  const savedSkip = saved?.skip_weekends ?? true

  // **A draft overlaid on the server's answer, rather than state seeded from it
  // by an effect.** The effect version is the obvious one and it is wrong twice
  // over: the first render after the query resolves still holds the initial
  // state, so both selects paint the first option — 00:00 — for a frame before
  // the effect corrects them, which is a window in which a tenant is shown hours
  // their organisation does not have. And a caller that acted in that frame
  // (`Save`, or a test) read the placeholder rather than the value.
  //
  // With `draft === null` meaning *untouched*, the very first render already
  // shows what the server holds, and a refetch that brings back a colleague's
  // change is picked up rather than overwritten by whatever this tab last
  // rendered. `save.run` clears it, which is what re-synchronises the form to
  // the row that was just written.
  const [draft, setDraft] = useState<Draft | null>(null)
  const start = draft?.start ?? savedStart ?? ''
  const end = draft?.end ?? savedEnd ?? ''
  const skip = draft?.skip ?? savedSkip

  const edit = (changes: Partial<Draft>): void => {
    setDraft({ start, end, skip, ...changes })
    save.reset()
  }

  const dirty = start !== savedStart || end !== savedEnd || skip !== savedSkip
  // The server refuses this with a 422 and says so; the button is disabled first
  // so the tenant finds out while they are still looking at the two fields that
  // caused it, rather than after a round trip.
  const ordered = start !== '' && end !== '' && start < end

  return (
    <div className="ar-card">
      <div className="ar-card__header">
        <h3>Send window</h3>
      </div>

      {automation.state.status === 'loading' ? (
        <div className="ar-card__body">
          <Loading label="Loading your send window" lines={2} />
        </div>
      ) : null}

      {automation.state.status === 'error' ? (
        <div className="ar-card__body">
          <ErrorState
            failure={automation.state.failure}
            title="Could not load your send window"
            onRetry={automation.refetch}
          />
        </div>
      ) : null}

      {automation.state.status === 'ready' ? (
        <>
          <div className="ar-card__body ar-stack">
            <Field
              label="Only act between"
              help="IST. Activity outside business hours is a strong ban signal."
              error={dirty && !ordered ? 'The start has to be before the end.' : undefined}
            >
              {(aria) => (
                <div className="ar-row">
                  <select
                    className="ar-select"
                    style={{ flex: 1 }}
                    {...aria}
                    disabled={!editable}
                    value={start}
                    onChange={(event) => {
                      edit({ start: event.target.value })
                    }}
                  >
                    {TIMES.map((time) => (
                      <option key={time} value={time}>
                        {time}
                      </option>
                    ))}
                  </select>
                  <span className="ar-muted">and</span>
                  <select
                    className="ar-select"
                    style={{ flex: 1 }}
                    aria-label="End of the send window"
                    disabled={!editable}
                    value={end}
                    onChange={(event) => {
                      edit({ end: event.target.value })
                    }}
                  >
                    {TIMES.map((time) => (
                      <option key={time} value={time}>
                        {time}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </Field>

            <label className="ar-switch">
              <input
                type="checkbox"
                disabled={!editable}
                checked={skip}
                onChange={(event) => {
                  edit({ skip: event.target.checked })
                }}
              />
              <span className="ar-switch__track" aria-hidden="true" />
              <span>
                Skip weekends
                <span className="ar-choice__desc">
                  Nothing searches or posts on Saturday or Sunday.
                </span>
              </span>
            </label>

            {/* The one thing this card must never do is imply it only governs
                searches. `admit_post` gates scheduled posts on the same window,
                and the mockup says so on the scheduling card at the other end of
                the product — a tenant who read only this one would be surprised
                by a post that did not go out. */}
            <p className="ar-subtle">
              Discovery searches and scheduled LinkedIn posts both wait for this window.
            </p>

            <InlineError failure={save.state.failure} />
          </div>

          {editable ? (
            <div className="ar-card__footer">
              <span className="ar-card__spacer" />
              <Button
                size="sm"
                disabled={!dirty || !ordered}
                busy={save.pending}
                onClick={() => {
                  void (async () => {
                    const result = await save.run({
                      window_start: start,
                      window_end: end,
                      skip,
                    })
                    if (!result.ok) return
                    // Back to *untouched*, so the form now reflects the row that
                    // was just written rather than a draft that happens to match
                    // it.
                    setDraft(null)
                    automation.refetch()
                    toast.show({
                      kind: 'success',
                      title: 'Send window saved',
                      body: skip
                        ? `Automation acts between ${start} and ${end} IST, weekdays only.`
                        : `Automation acts between ${start} and ${end} IST, every day.`,
                    })
                  })()
                }}
              >
                Save
              </Button>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  )
}

/**
 * `09:30:00` from the API as `09:30` for a `<select>` value.
 *
 * FastAPI serialises a `datetime.time` with seconds and the options here do not
 * carry them, so without this every value arrives unmatched and both selects
 * render blank — showing a tenant an empty window over a server that has one.
 */
/** What the tenant has changed but not yet saved. `null` until they touch one. */
interface Draft {
  start: string
  end: string
  skip: boolean
}

function trimSeconds(time: string): string {
  return time.slice(0, 5)
}

/**
 * Every half hour of the day, as `HH:MM`.
 *
 * `23:30` is the last entry rather than `23:59`, which means the *literal*
 * always-on window `app/linkedin/send_window.py` describes is not selectable
 * here. That is deliberate: 00:00 to 23:30 differs from around-the-clock by half
 * an hour at the quietest end of the night, nobody schedules outreach in it, and
 * the alternative is one odd option in a list of forty-eight that exists only to
 * express *turn this protection off*. A tenant who genuinely needs it can be set
 * up by support against the API.
 */
const TIMES: readonly string[] = Array.from({ length: 48 }, (_, index) => {
  const hour = Math.floor(index / 2)
  const minute = index % 2 === 0 ? '00' : '30'
  return `${String(hour).padStart(2, '0')}:${minute}`
})
